import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { useNavigate, useLocation } from "react-router-dom";

function Chip({ children, tone = "gray" }) {
  const bg =
    tone === "blue"
      ? "rgba(13,110,253,0.12)"
      : tone === "green"
      ? "rgba(25,135,84,0.12)"
      : tone === "red"
      ? "rgba(220,53,69,0.12)"
      : tone === "orange"
      ? "rgba(255,193,7,0.18)"
      : "rgba(0,0,0,0.04)";

  const border =
    tone === "blue"
      ? "rgba(13,110,253,0.22)"
      : tone === "green"
      ? "rgba(25,135,84,0.22)"
      : tone === "red"
      ? "rgba(220,53,69,0.22)"
      : tone === "orange"
      ? "rgba(255,193,7,0.35)"
      : "rgba(0,0,0,0.08)";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        background: bg,
        border: `1px solid ${border}`,
        fontSize: 12,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Card({ title, right, children, style }) {
  return (
    <div className="card" style={{ padding: 18, borderRadius: 14, ...style }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 20 }}>{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function formatOfferingLabel(o) {
  if (!o) return "Unknown offering";

  const course = o.course_code
    ? `${o.course_code} — ${o.course_name || ""}`
    : o.title || "Course Offering";

  const semester = `${o.semester || "-"} ${o.session || ""}`.trim();
  const section = `${o.batch || "-"} ${o.section || ""}`.trim();

  return `${course} | ${semester} | ${section} | ${
    o.instructor_name || "Instructor"
  }`;
}

function getBackendError(e, fallback) {
  const detail = e?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.message || "Validation error").join(", ");
  }

  if (typeof detail === "string") return detail;

  return fallback;
}

export default function Assessments() {
  const [offerings, setOfferings] = useState([]);
  const [offeringId, setOfferingId] = useState("");
  const [items, setItems] = useState([]);

  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingOfferings, setLoadingOfferings] = useState(false);
  const [loadingAssessments, setLoadingAssessments] = useState(false);

  const [type, setType] = useState("quiz");
  const [title, setTitle] = useState("");
  const [maxMarks, setMaxMarks] = useState(10);
  const [weightage, setWeightage] = useState(10);

  const todayStr = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr);

  const nav = useNavigate();
  const location = useLocation();

  const queryOfferingId = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("offeringId") || "";
  }, [location.search]);

  const queryCourseId = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("courseId") || "";
  }, [location.search]);

  const selectedOffering = useMemo(
    () => offerings.find((o) => o.id === offeringId),
    [offerings, offeringId]
  );

  const loadOfferings = async () => {
    setErr("");
    setLoadingOfferings(true);
    setOfferings([]);
    setItems([]);

    try {
      const res = await api.get("/course-offerings/my");
      const list = res.data || [];

      setOfferings(list);

      let preferred = "";

      if (queryOfferingId && list.some((o) => o.id === queryOfferingId)) {
        preferred = queryOfferingId;
      } else if (queryCourseId) {
        const match = list.find((o) => o.course_id === queryCourseId);
        preferred = match?.id || "";
      } else {
        preferred = list[0]?.id || "";
      }

      setOfferingId(preferred);
    } catch (e) {
      setErr(getBackendError(e, "Failed to load course offerings"));
    } finally {
      setLoadingOfferings(false);
    }
  };

  const loadAssessments = async (targetOfferingId = offeringId) => {
    if (!targetOfferingId) {
      setItems([]);
      return;
    }

    setErr("");
    setLoadingAssessments(true);
    setItems([]);

    try {
      const res = await api.get(`/offerings/${targetOfferingId}/assessments`);
      setItems(res.data || []);
    } catch (e) {
      setErr(getBackendError(e, "Failed to load assessments"));
    } finally {
      setLoadingAssessments(false);
    }
  };

  useEffect(() => {
    loadOfferings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryOfferingId, queryCourseId]);

  useEffect(() => {
    if (!offeringId) {
      setItems([]);
      return;
    }

    loadAssessments(offeringId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offeringId]);

  const stats = useMemo(() => {
    const byType = { quiz: 0, assignment: 0, mid: 0, final: 0 };

    (items || []).forEach((a) => {
      const t = (a.type || "").toLowerCase();

      if (byType[t] !== undefined) {
        byType[t] += 1;
      }
    });

    return byType;
  }, [items]);

  const create = async () => {
    setErr("");
    setBusy(true);

    try {
      const payload = {
        type,
        title,
        max_marks: Number(maxMarks),
        weightage: Number(weightage),
        date,
      };

      const res = await api.post(
        `/offerings/${offeringId}/assessments`,
        payload
      );

      const created = res.data;

      setTitle("");
      setMaxMarks(10);
      setWeightage(10);
      setDate(todayStr);

      setItems((prev) => [created, ...prev]);
    } catch (e) {
      setErr(getBackendError(e, "Create failed"));
    } finally {
      setBusy(false);
    }
  };

  const canCreate =
    !!offeringId &&
    !!title.trim() &&
    Number.isFinite(Number(maxMarks)) &&
    Number(maxMarks) > 0 &&
    Number.isFinite(Number(weightage)) &&
    Number(weightage) >= 0 &&
    !!date;

  return (
    <div className="page" style={{ maxWidth: 1150, margin: "0 auto" }}>
      <Card
        title="Assessments"
        right={
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Chip tone="blue">Total: {items.length}</Chip>
            <Chip>Quizzes: {stats.quiz}</Chip>
            <Chip>Assignments: {stats.assignment}</Chip>
            <Chip>Mid: {stats.mid}</Chip>
            <Chip>Final: {stats.final}</Chip>
          </div>
        }
      >
        {err && (
          <div className="alert alert-danger" style={{ marginTop: 10 }}>
            {err}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 12,
            alignItems: "end",
          }}
        >
          <div className="field">
            <label>Course Offering / Class Section</label>

            <select
              className="select"
              value={offeringId}
              onChange={(e) => {
                setOfferingId(e.target.value);
                setItems([]);
                setErr("");
              }}
              disabled={loadingOfferings || busy}
            >
              <option value="">
                {loadingOfferings
                  ? "Loading offerings..."
                  : "Select course offering..."}
              </option>

              {offerings.map((o) => (
                <option key={o.id} value={o.id}>
                  {formatOfferingLabel(o)}
                </option>
              ))}
            </select>
          </div>

          <button
            className="btn"
            onClick={() => loadAssessments(offeringId)}
            disabled={!offeringId || busy || loadingAssessments}
            style={{ height: 40 }}
          >
            {loadingAssessments ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {selectedOffering ? (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 12,
              background: "rgba(13,110,253,0.06)",
              border: "1px solid rgba(13,110,253,0.16)",
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Chip tone="blue">
              {selectedOffering.course_code} — {selectedOffering.course_name}
            </Chip>
            <Chip>{selectedOffering.semester} {selectedOffering.session}</Chip>
            <Chip>{selectedOffering.batch} {selectedOffering.section}</Chip>
            <Chip tone="green">
              Instructor: {selectedOffering.instructor_name || "-"}
            </Chip>
            <Chip>
              Students: {selectedOffering.students_count ?? 0}
            </Chip>
          </div>
        ) : (
          <div className="muted" style={{ marginTop: 12, fontSize: 13 }}>
            No course offering selected. Ask Admin to create a course offering
            and assign you as instructor/course lead.
          </div>
        )}

        <div
          style={{
            marginTop: 14,
            padding: 14,
            borderRadius: 14,
            border: "1px solid rgba(0,0,0,0.06)",
            background: "rgba(255,255,255,0.65)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontWeight: 800 }}>Create new assessment</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                This assessment will belong only to the selected offering/class
                section. Use titles like <b>Q_1</b>, <b>A_1</b> for easy demo.
              </div>
            </div>

            <Chip tone={canCreate ? "green" : "red"}>
              {canCreate ? "Ready" : "Missing fields"}
            </Chip>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gap: 12,
            }}
          >
            <div className="field" style={{ gridColumn: "span 3" }}>
              <label>Type</label>
              <select
                className="select"
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={busy}
              >
                <option value="quiz">Quiz</option>
                <option value="assignment">Assignment</option>
                <option value="mid">Mid</option>
                <option value="final">Final</option>
              </select>
            </div>

            <div className="field" style={{ gridColumn: "span 5" }}>
              <label>Title</label>
              <input
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Q_1 — Week 2 Quiz"
                disabled={busy}
              />
            </div>

            <div className="field" style={{ gridColumn: "span 2" }}>
              <label>Max Marks</label>
              <input
                className="input"
                type="number"
                min="1"
                value={maxMarks}
                onChange={(e) => setMaxMarks(e.target.value)}
                disabled={busy}
              />
            </div>

            <div className="field" style={{ gridColumn: "span 2" }}>
              <label>Weightage</label>
              <input
                className="input"
                type="number"
                min="0"
                value={weightage}
                onChange={(e) => setWeightage(e.target.value)}
                disabled={busy}
              />
            </div>

            <div className="field" style={{ gridColumn: "span 3" }}>
              <label>Date</label>
              <input
                className="input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={busy}
              />
            </div>

            <div
              style={{
                gridColumn: "span 9",
                display: "flex",
                gap: 10,
                alignItems: "end",
                flexWrap: "wrap",
              }}
            >
              <button
                className="btn-primary"
                onClick={create}
                disabled={busy || !canCreate}
                style={{ height: 40 }}
              >
                {busy ? "Creating..." : "Create"}
              </button>

              <button
                className="btn"
                onClick={() => {
                  setType("quiz");
                  setTitle("");
                  setMaxMarks(10);
                  setWeightage(10);
                  setDate(todayStr);
                }}
                disabled={busy}
                style={{ height: 40 }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </Card>

      <Card
        title="Assessment List"
        style={{ marginTop: 16 }}
        right={
          <span className="muted" style={{ fontSize: 12 }}>
            {loadingAssessments
              ? "Loading selected offering assessments..."
              : "Open to upload questions & grade"}
          </span>
        }
      >
        <div style={{ overflowX: "auto" }}>
          <table className="table" style={{ marginTop: 0 }}>
            <thead>
              <tr>
                <th style={{ width: 120 }}>Type</th>
                <th>Title</th>
                <th style={{ width: 90 }}>Marks</th>
                <th style={{ width: 110 }}>Weightage</th>
                <th style={{ width: 120 }}>Date</th>
                <th style={{ width: 140 }}>Scope</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>

            <tbody>
              {items.map((a) => (
                <tr key={a.id}>
                  <td>
                    <Chip tone="blue">{a.type}</Chip>
                  </td>

                  <td style={{ fontWeight: 700 }}>{a.title}</td>

                  <td>{a.max_marks}</td>

                  <td>{a.weightage ?? "-"}</td>

                  <td>{a.date ? String(a.date).slice(0, 10) : "-"}</td>

                  <td>
                    <Chip tone={a.offering_id ? "green" : "orange"}>
                      {a.offering_id ? "Offering" : "Legacy Course"}
                    </Chip>
                  </td>

                  <td>
                    <button
                      className="btn"
                      onClick={() => nav(`/assessments/${a.id}`)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}

              {!items.length && (
                <tr>
                  <td colSpan={7} className="muted" style={{ padding: 16 }}>
                    {loadingAssessments
                      ? "Loading assessments..."
                      : offeringId
                      ? "No assessments yet for this offering."
                      : "Select a course offering first."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}